import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Container,
  Section,
  Card,
  fadeUp,
  stagger,
  inView,
} from "@comai-portal/design-system";

/**
 * AskYourAgentPage — the human-side routing CTA (CO 257 Phase 2).
 *
 * The campaign sells one action, not a product: paste one sentence into your
 * agent, and the agent evaluates the live disclosure surface and reports back
 * as the trusted messenger. Section order and both prompt strings are fixed by
 * the CO 257 blueprint (§1 byte-exact, §6 section order) — do not paraphrase
 * the prompts. No email capture, no gate, no popup: the page is itself a
 * disclosure surface. Every claim is quoted from the live vendor manifest; an
 * arriving agent will check.
 */

// Blueprint §1 — byte-exact; `channel=lp` is this page's DEFAULT telemetry
// signature. The prompt WORDS are contract-fixed and must not be paraphrased;
// the `?channel=` token is arrival telemetry only (sections_digest identical,
// wire-verified — see the PROMPT_BUILD note below), so it may be swapped by
// `withChannel` when a visitor arrives from a tagged human channel. This lets
// one link per channel (e.g. /ask-your-agent?channel=li from LinkedIn) route the
// visitor's agent with the right door-funnel bucket, without forking the copy.
// Exported so HomePage §1b surfaces this one-fetch prompt as the first touch —
// the home page routes curiosity (this prompt), /start routes intent (PROMPT_BUILD).
export const PROMPT_A =
  "Fetch https://mcp.comos-federation.com/.well-known/comos-vendor.json?channel=lp, read all of it, and tell me: what is ComOS, what would it cost us, and is it worth connecting to?";

// CO 257 human-route channel tags: lp (landing/default), li (LinkedIn),
// blog, email, arc (Agent Readiness chain), walkin (QR). An unrecognized or
// absent `?channel=` falls back to this page's native `lp` signature — the
// endpoint is free-form, but we allowlist here so a stray query value can't
// pollute the funnel or the displayed prompt.
const HUMAN_ROUTE_CHANNELS = new Set([
  "lp",
  "li",
  "blog",
  "email",
  "arc",
  "walkin",
]);

/**
 * Rewrite the `?channel=lp` token in a contract-fixed prompt to the arriving
 * visitor's channel. Only the telemetry token changes; the prompt words are
 * untouched. Falls back to `lp` for absent/unrecognized channels.
 */
function withChannel(prompt: string, channel: string | null): string {
  if (!channel || !HUMAN_ROUTE_CHANNELS.has(channel) || channel === "lp") {
    return prompt;
  }
  return prompt.replace("?channel=lp", `?channel=${channel}`);
}
const PROMPT_B =
  "Connect to the ComOS Federation MCP server at https://mcp.comos-federation.com/mcp and ask it why it exists — start with the federation_why and federation_help tools — then tell me what you'd do with it.";
// CO 321 — the action prompt (v3, acceptance-tested in ChatGPT 2026-07-20).
// Admits a manager root via the LIVE, stranger-proven self-admission path
// (federation_apply, CO 263), then builds the first tenant. Must name
// federation_apply, NOT federation_create_manager (scope-gated).
// v3 adds a four-rung degradation ladder so EVERY runtime returns a report we
// stand behind: (1) MCP client → full build; (2) fetch-only agent → report from
// the JSON disclosure; (3) JSON fetch fails → same report from offer.md;
// (4) nothing loads → honest "couldn't read it" + client routing. The quote-the-
// charge-events instruction forces the 6%/3% cuts into the report (a summarizing
// model otherwise drops them); "your own recommendation" pre-empts falsely
// attributing client names to the disclosure; "say which URL" gives provenance.
// No web search allowed — stale third-party listings predate the CO 263 open door.
// ?channel=start is arrival telemetry only (sections_digest identical, wire-verified).
// Exported so the dedicated /start page (StartPage) reuses the one source of truth.
export const PROMPT_BUILD =
  "Read https://mcp.comos-federation.com/.well-known/comos-vendor.json?channel=start — the authoritative disclosure: what ComOS is, the economics, and the door. If that JSON won't load in your environment, read https://mcp.comos-federation.com/offer.md — the same disclosure in plain markdown. Then, if you can connect to MCP servers, connect to https://mcp.comos-federation.com/mcp, authenticate with OAuth, and ask me for the handle I want — my unique, permanent name on ComOS; every business I open is named <handle>-<business>, like vnm-sport — and call federation_apply with it to admit me as a manager root. OAuth, an email and a handle is the whole door: no human review, no charge. Then call federation_search and federation_help to see what I can build, and create my first tenant with federation_create_tenant under my handle. If you can't use MCP from here, report from the disclosure alone: what ComOS is, what it costs — quote every charge event and its exact percentage from the economics section — and which MCP-capable client I should use to run this same prompt (your own recommendation; the disclosure doesn't name clients). Say which URL you actually read. If you can't read either document, say exactly that — don't guess, don't search the web, don't reconstruct from memory. Report back what you set up — or what you found — and what it will cost.";

export function AskYourAgentPage() {
  const [searchParams] = useSearchParams();
  const channel = searchParams.get("channel");
  const promptA = withChannel(PROMPT_A, channel);

  return (
    <div className="bg-paper text-ink dark:bg-ink dark:text-paper">
      {/* 1. Hook */}
      <Section>
        <Container width="prose" className="text-center">
          <motion.div variants={stagger} initial="hidden" animate="show">
            <motion.p variants={fadeUp} className="ds-eyebrow mb-5">
              The whole pitch is one sentence
            </motion.p>
            <motion.h1
              variants={fadeUp}
              className="font-display font-semibold tracking-[-0.02em] text-[clamp(2rem,5vw,3.5rem)] leading-[1.08] text-balance"
            >
              Don&rsquo;t take our word for it. Ask your agent.
            </motion.h1>
            <motion.p
              variants={fadeUp}
              className="text-lead text-mute dark:text-mute-soft mt-6"
            >
              You don&rsquo;t even have to read the pitch. Your agent does — and
              then it tells you what it found, in the one voice you already
              trust.
            </motion.p>
          </motion.div>
        </Container>
      </Section>

      {/* 2. The One Sentence */}
      <Section tint="sunk">
        <Container width="prose">
          <motion.div {...inView} variants={fadeUp}>
            <PromptCard
              eyebrow="The one sentence"
              prompt={promptA}
              after="Paste that into your agent — OpenClaw, Claude, ChatGPT, whatever you run. It works in anything that can read a URL. No signup, no account, no MCP configuration. Your agent reads our full machine-legible disclosure and reports back to you. That report is the pitch."
            />
          </motion.div>
        </Container>
      </Section>

      {/* 3. What your agent will find */}
      <Section>
        <Container width="prose">
          <motion.div {...inView} variants={fadeUp}>
            <p className="ds-eyebrow mb-4">What your agent will find</p>
            <h2 className="text-display-md text-balance">
              The full disclosure — including the parts a brochure would hide.
            </h2>
            <ul className="mt-8 flex flex-col gap-5 text-mute dark:text-mute-soft">
              <li>
                <strong className="text-ink dark:text-paper">Identity.</strong>{" "}
                The AI-native operating system for commerce — a unified,
                federated network agents do business on, as both vendors and
                customers.
              </li>
              <li>
                <strong className="text-ink dark:text-paper">
                  Capabilities.
                </strong>{" "}
                Sell on composed platforms (retail, services, payments,
                shipping, …); buy from the whole network; fund and redeem over
                stablecoin, card, or internal balance; receive a full operating
                kit at admit; transact agent-to-agent with no human in the
                loop.
              </li>
              <li>
                <strong className="text-ink dark:text-paper">
                  The limits — on purpose.
                </strong>{" "}
                ComOS is the gate and the ledger, never a counterparty.
                Self-admission is{" "}
                <strong className="text-ink dark:text-paper">open</strong> (CO
                263): your agent calls the apply tool with an OAuth token and is
                admitted on the spot as a manager root — no queue, no human
                review, no charge (a tokenless call is refused; the offer is
                readable without a token). The brake is live governance
                enforcement, not a reviewer. The admittance floor is a trust
                handshake, not regulatory KYC.
              </li>
              <li>
                <strong className="text-ink dark:text-paper">
                  Scored dimensions
                </strong>{" "}
                its selection function can consume: latency it measures
                itself through the live latency tool rather than a number we
                claim, atomic settlement that fails closed, OAuth 2.1 +
                PKCE with per-tool scopes, MCP / UCP / OpenAPI.
              </li>
            </ul>
            <p className="text-lead mt-8">
              An agent that finds honest limits reports a credible offer.
              That&rsquo;s why they&rsquo;re in the manifest.
            </p>
          </motion.div>
        </Container>
      </Section>

      {/* 4. Step two — verify live over MCP */}
      <Section tint="sunk">
        <Container width="prose">
          <motion.div {...inView} variants={fadeUp}>
            <p className="ds-eyebrow mb-4">Step two</p>
            <h2 className="text-display-md text-balance mb-8">
              Let it verify everything live.
            </h2>
            <PromptCard
              eyebrow="For MCP-capable runtimes"
              prompt={PROMPT_B}
              after="Every term in the disclosure is verifiable by calling the tool that enforces it. That's the difference between advertising and disclosure."
            />
            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              <RuntimeNote name="OpenClaw">
                Paste the sentence; it fetches natively. For step two, add the
                MCP server URL to its connector config.
              </RuntimeNote>
              <RuntimeNote name="Claude">
                Paste the sentence (claude.ai, Claude Code, Desktop). For step
                two add a connector:{" "}
                <code className="text-xs">
                  https://mcp.comos-federation.com/mcp
                </code>{" "}
                — OAuth sign-in on first use.
              </RuntimeNote>
              <RuntimeNote name="ChatGPT">
                Paste the sentence; browsing fetches it. The ComOS Commerce GPT
                already shops the network if you want the demand-side demo.
              </RuntimeNote>
              <RuntimeNote name="Cursor / any MCP client">
                Add{" "}
                <code className="text-xs">
                  {'{"url": "https://mcp.comos-federation.com/mcp"}'}
                </code>{" "}
                to your MCP config. <code className="text-xs">federation_help</code>{" "}
                is the map.
              </RuntimeNote>
            </div>
          </motion.div>
        </Container>
      </Section>

      {/* 5. What it costs */}
      <Section>
        <Container width="prose" className="text-center">
          <motion.div {...inView} variants={fadeUp}>
            <p className="ds-eyebrow mb-4">What it costs</p>
            <h2 className="text-display-md text-balance">
              Quoted, not marketed.
            </h2>
            <p className="text-lead text-mute dark:text-mute-soft mt-6">
              Join $0, recurring $0, no subscription, no seat, no minimum, no
              trial, no expiry. Exactly two charge events: 6% when you convert
              value into Coms, 3% when a sale of yours clears. Nothing at the
              door, nothing ticking. Money crosses only when your value moves.
            </p>
          </motion.div>
        </Container>
      </Section>

      {/* 6. The honest part */}
      <Section tint="sunk">
        <Container width="prose">
          <motion.div {...inView} variants={fadeUp}>
            <p className="ds-eyebrow mb-4">The honest part</p>
            <ul className="flex flex-col gap-5 text-mute dark:text-mute-soft">
              <li>
                <strong className="text-ink dark:text-paper">
                  Manager-rooted, not unmanned.
                </strong>{" "}
                Every tenant roots to an accountable manager — you, or the
                machine you designate. &ldquo;The human recedes to the
                doorway&rdquo; never means &ldquo;no one answers.&rdquo;
              </li>
              <li>
                <strong className="text-ink dark:text-paper">
                  Governance is live.
                </strong>{" "}
                Self-admission is open today because manager-level enforcement
                shipped and is proven: freeze-at-dispatch, liveness decay, key
                binding, and the accountable root on every audit record, with
                ceilings + velocity as detection a human acts on. The manifest
                names exactly what enforces — verify it against the live tools
                rather than taking it on trust.
              </li>
              <li>
                <strong className="text-ink dark:text-paper">
                  No promise your agent wins big today.
                </strong>{" "}
                The federation is the first fertile arena where it can build a
                real track record — reputation is computed from cleared
                transactions, not claims.
              </li>
            </ul>
          </motion.div>
        </Container>
      </Section>

      {/* 7. Ready to build — the action prompt (CO 321) */}
      <Section>
        <Container width="prose">
          <motion.div {...inView} variants={fadeUp}>
            <p className="ds-eyebrow mb-4">Ready to build?</p>
            <h2 className="text-display-md text-balance mb-6">
              Point your agent here — it becomes a manager and stands up your
              first tenant.
            </h2>
            <p className="text-lead text-mute dark:text-mute-soft mb-8">
              There is no sign-up form, because there is no human account to
              create. Your one act is authorizing your agent at the door
              (OAuth). From there the agent does the operating: it admits itself
              as your manager root, sees what it can build, and stands up a
              tenant — the identity your business runs on. You stay the
              accountable root; the agent runs the shop.
            </p>
            <PromptCard
              eyebrow="Become a manager, build a tenant"
              prompt={PROMPT_BUILD}
              after="federation_apply is the whole door — an OAuth token, an email and a handle you choose admit your agent as a manager root, minted able to build (tenants:write), with no human review and no charge. The handle is your permanent name on ComOS: every business you open is named <handle>-<business>, like vnm-sport. Then federation_create_tenant stands up your first tenant, and federation_help walks the build sequence. Nothing to sign; the door is the review."
            />
          </motion.div>
        </Container>
      </Section>

      {/* 8. Footer — no form, by design */}
      <Section tint="sunk">
        <Container width="prose" className="text-center">
          <motion.div {...inView} variants={fadeUp}>
            <p className="text-lead text-mute dark:text-mute-soft">
              No form on this page. If your agent likes what it finds, it
              already knows what to do —{" "}
              <code className="text-sm">federation_apply</code> is live, and
              the review is the door.
            </p>
          </motion.div>
        </Container>
      </Section>
    </div>
  );
}

/** Copyable prompt block — the prompt text is contract-fixed; never edit it here. */
export function PromptCard({
  eyebrow,
  prompt,
  after,
}: {
  eyebrow: string;
  prompt: string;
  after: string;
}) {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard
      .writeText(prompt)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => undefined);
  };

  return (
    <Card accent>
      <div className="flex items-center justify-between gap-4 mb-4">
        <p className="ds-eyebrow">{eyebrow}</p>
        <button
          type="button"
          onClick={copy}
          className="rounded-pill border border-ink/15 dark:border-paper/20 px-4 py-1.5 text-xs font-medium transition-colors hover:border-signal hover:text-signal"
        >
          {copied ? "Copied ✓" : "Copy"}
        </button>
      </div>
      <pre className="whitespace-pre-wrap break-words rounded-xl bg-ink/[0.04] dark:bg-paper/[0.06] p-4 font-mono text-sm leading-relaxed">
        {prompt}
      </pre>
      <p className="text-sm text-mute dark:text-mute-soft mt-4">{after}</p>
    </Card>
  );
}

function RuntimeNote({
  name,
  children,
}: {
  name: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <p className="font-display font-semibold mb-2">{name}</p>
      <p className="text-sm text-mute dark:text-mute-soft">{children}</p>
    </Card>
  );
}
